import { resourceService } from '@/services/resource.service'
import type { ResourceBrief } from '@/types'
import { FileText, Loader2, Table2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'

/** Card chrome: `ring` does not shrink the content box (unlike `border`), so 16:9 math matches the borderless tile. */
function ResourceCardBackground({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-border transition-colors group-hover:bg-accent">
      {children}
    </div>
  )
}

export function ResourceCard({
  resource,
  onOpenPdf,
}: {
  resource: ResourceBrief
  onOpenPdf: (resource: ResourceBrief) => void | Promise<void>
}) {
  const [downloading, setDownloading] = useState(false)
  const [imageFailed, setImageFailed] = useState(false)

  const runDownload = async () => {
    if (downloading) return
    setDownloading(true)
    try {
      await resourceService.triggerResourceDownload(resource.id, resource.title)
    } catch {
      alert('Unable to download this file right now. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  const handleMainClick = () => {
    if (downloading) return
    if (resource.file_type === 'pdf') {
      void onOpenPdf(resource)
    } else {
      void runDownload()
    }
  }

  return (
    <div className="group flex h-full min-h-0 w-full flex-col text-left">
      <ResourceCardBackground>
        <button
          type="button"
          onClick={handleMainClick}
          disabled={downloading}
          className="flex min-h-0 w-full flex-1 flex-col border-0 bg-transparent p-0 text-left shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-70"
        >
          <div className="relative aspect-[16/9] w-full min-w-0 shrink-0 overflow-hidden bg-muted">
            {resource.cover_url && !imageFailed ? (
              <img
                src={resource.cover_url}
                alt=""
                className="h-full w-full object-cover"
                onError={() => setImageFailed(true)}
              />
            ) : null}
            <div
              className={`absolute inset-0 flex items-center justify-center ${resource.cover_url && !imageFailed ? 'hidden' : ''}`}
            >
              {resource.file_type === 'pdf' ? (
                <FileText className="h-8 w-8 text-muted-foreground" aria-hidden />
              ) : (
                <Table2 className="h-8 w-8 text-muted-foreground" aria-hidden />
              )}
            </div>
          </div>
          <div className="flex min-h-0 flex-1 flex-col p-3">
            <h3 className="line-clamp-2 text-sm font-normal leading-snug text-foreground">
              {resource.title}
            </h3>
            <div className="mt-1 min-h-[2.5rem] flex-1">
              {resource.description ? (
                <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                  {resource.description}
                </p>
              ) : null}
            </div>
            {downloading ? (
              <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Downloading...
              </div>
            ) : null}
          </div>
        </button>
      </ResourceCardBackground>
    </div>
  )
}
